import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/app/auth";
import { dateTime } from "@/lib/format";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

type Level = "debug" | "info" | "warn" | "error";


const LEVELS: Level[] = ["error", "warn", "info", "debug"];
const SOURCES = ["web", "edge", "webhook", "cron", "worker", "db"];
const LEVEL_CLASS: Record<Level, string> = {
  error: "text-destructive", warn: "text-amber-400", info: "text-sky-400", debug: "text-muted-foreground",
};

interface CronRow { job: string; schedule: string; active: boolean; last_run: string | null; last_status: string | null; last_message: string | null }
interface AuthRow { at: string; action: string | null; email: string | null; ip: string | null; device: string | null; source: string }
type Summary = Record<string, number>;

/** Une expression cron ne dit rien à personne : on écrit la fréquence en français, l'expression brute reste en infobulle. */
function scheduleLabel(cron: string): string {
  const m = cron.trim().split(/\s+/);
  if (m.length !== 5) return cron;
  const [min, hour, dom, mon, dow] = m;
  const h2 = (h: string, mi: string) => `${h.padStart(2, "0")} h ${mi.padStart(2, "0")}`;
  if (dom === "*" && mon === "*" && dow === "*") {
    if (hour === "*" && /^\*\/(\d+)$/.test(min)) return `toutes les ${min.slice(2)} minutes`;
    if (hour === "*" && /^\d+$/.test(min)) return `chaque heure, à la minute ${Number(min)}`;
    if (/^\*\/(\d+)$/.test(hour) && /^\d+$/.test(min)) return `toutes les ${hour.slice(2)} heures`;
    if (/^\d+$/.test(hour) && /^\d+$/.test(min)) return `chaque jour à ${h2(hour, min)}`;
  }
  return cron;
}
const CRON_RESULT: Record<string, string> = { succeeded: "réussi", failed: "échec", running: "en cours" };

/** L'agent du navigateur en clair : « Chrome sur macOS » plutôt que 120 caractères techniques. */
function deviceLabel(ua: string | null): string {
  if (!ua) return "—";
  const nav = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Navigateur";
  const os = /iPhone|iPad/.test(ua) ? "iPhone/iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${nav} sur ${os}` : nav;
}

function Tile({ label, value, tone, hint }: { label: string; value: number | string; tone?: "alert"; hint?: string }) {
  return (
    <div className="rounded-xl bg-muted/60 p-4">
      <div className={cn("font-heading text-2xl font-bold", tone === "alert" && "text-destructive")}>{value}</div>
      <div className="text-sm">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/** Synthèse 24 h / 7 j, état des tâches planifiées et connexions récentes. */
function HealthTab() {
  const { roles } = useAuth();
  const isAdmin = roles.includes("admin");
  const { data: summary } = useQuery({
    queryKey: ["console_summary"], refetchInterval: 60_000,
    queryFn: async () => { const { data, error } = await supabase.rpc("console_summary"); if (error) throw error; return (data ?? {}) as unknown as Summary; },
  });
  const { data: cron } = useQuery({
    queryKey: ["cron_status"], refetchInterval: 60_000,
    queryFn: async () => { const { data, error } = await supabase.rpc("cron_status"); if (error) throw error; return (data ?? []) as unknown as CronRow[]; },
  });
  const { data: auth } = useQuery({
    queryKey: ["auth_log"], enabled: isAdmin, refetchInterval: 60_000,
    queryFn: async () => { const { data, error } = await supabase.rpc("auth_log", { _limit: 30 }); if (error) throw error; return (data ?? []) as unknown as AuthRow[]; },
  });
  const n = (k: string) => Number(summary?.[k] ?? 0);

  return (
    <div className="space-y-4">
      <section className="glass rounded-2xl p-5" data-testid="console-health">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Santé du CRM</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Tile label="Erreurs (24 h)" value={n("errors_24h")} tone={n("errors_24h") > 0 ? "alert" : undefined} hint={`${n("warns_24h")} avertissement(s), ${n("logs_24h")} entrée(s)`} />
          <Tile label="Événements Stripe (7 j)" value={n("stripe_events_7d")} hint={n("stripe_events_failed") > 0 ? `${n("stripe_events_failed")} en erreur` : "aucun en erreur"} />
          <Tile label="Événements signature (7 j)" value={n("signature_events_7d")} hint="webhooks de signature reçus" />
          <Tile label="Emails envoyés (7 j)" value={n("emails_7d")} tone={n("emails_failed_7d") > 0 ? "alert" : undefined} hint={n("emails_failed_7d") > 0 ? `${n("emails_failed_7d")} échec(s)` : "aucun échec"} />
        </div>
      </section>

      <section className="glass rounded-2xl p-5" data-testid="console-cron">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tâches planifiées</h2>
        <p className="mb-3 text-sm text-muted-foreground">Elles tournent sur la base, sans intervention. Un « échec » répété mérite un coup d'œil aux journaux ci-contre.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground"><th className="py-1 text-left font-medium">Tâche</th><th className="py-1 text-left font-medium">Fréquence</th><th className="py-1 text-left font-medium">Dernière exécution</th><th className="py-1 text-left font-medium">Résultat</th></tr></thead>
            <tbody>
              {(cron ?? []).map((c) => (
                <tr key={c.job} className="border-t border-border/60">
                  <td className="py-1.5 font-medium">{c.job}{!c.active && <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>}</td>
                  <td className="py-1.5 text-muted-foreground" title={c.schedule}>{scheduleLabel(c.schedule)}</td>
                  <td className="py-1.5">{c.last_run ? dateTime(c.last_run) : "jamais"}</td>
                  <td className={cn("py-1.5", c.last_status && c.last_status !== "succeeded" && "text-destructive")} title={c.last_message ?? ""}>{c.last_status ? CRON_RESULT[c.last_status] ?? c.last_status : "—"}</td>
                </tr>
              ))}
              {cron && cron.length === 0 && <tr><td colSpan={4} className="py-3 text-muted-foreground">Aucune tâche planifiée.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {isAdmin && (
        <section className="glass rounded-2xl p-5" data-testid="console-auth">
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Connexions récentes</h2>
          <p className="mb-3 text-sm text-muted-foreground">Sessions ouvertes et événements d'authentification conservés par Supabase (ces derniers sont purgés très vite, la liste peut donc n'afficher que les sessions). Visible par les administrateurs seulement.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground"><th className="py-1 text-left font-medium">Quand</th><th className="py-1 text-left font-medium">Action</th><th className="py-1 text-left font-medium">Compte</th><th className="py-1 text-left font-medium">IP</th><th className="py-1 text-left font-medium">Appareil</th></tr></thead>
              <tbody>
                {(auth ?? []).map((a, i) => (
                  <tr key={`${a.at}-${i}`} className="border-t border-border/60">
                    <td className="py-1.5">{dateTime(a.at)}</td>
                    <td className={cn("py-1.5", a.action?.includes("fail") && "text-destructive")}>{a.action ?? "—"}</td>
                    <td className="py-1.5 text-muted-foreground">{a.email ?? "—"}</td>
                    <td className="py-1.5 font-mono text-xs text-muted-foreground">{a.ip ?? "—"}</td>
                    <td className="max-w-[18rem] truncate py-1.5 text-xs text-muted-foreground" title={a.device ?? ""}>{deviceLabel(a.device)}</td>
                  </tr>
                ))}
                {auth && auth.length === 0 && <tr><td colSpan={5} className="py-3 text-muted-foreground">Aucune connexion enregistrée.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function LogsTab() {
  const [levels, setLevels] = useState<Level[]>(["error", "warn", "info"]);
  const [sources, setSources] = useState<string[]>(SOURCES);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<number | null>(null);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["system_logs", levels, sources, q],
    queryFn: async () => {
      let query = supabase.from("system_logs").select("*").order("created_at", { ascending: false }).limit(200)
        .in("level", levels).in("source", sources);
      if (q.trim()) query = query.ilike("message", `%${q.trim()}%`);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });

  const toggle = <T,>(list: T[], v: T, set: (l: T[]) => void) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div className="flex h-full min-h-[60vh] flex-col gap-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>Rafraîchir</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {LEVELS.map((l) => (
          <button key={l} onClick={() => toggle(levels, l, setLevels)} className={cn("rounded-md border px-2 py-1 text-xs uppercase", levels.includes(l) ? "border-primary bg-primary/10" : "border-border text-muted-foreground")}>{l}</button>
        ))}
        <span className="mx-2 h-4 w-px bg-border" />
        {SOURCES.map((s) => (
          <button key={s} onClick={() => toggle(sources, s, setSources)} className={cn("rounded-md border px-2 py-1 text-xs", sources.includes(s) ? "border-primary bg-primary/10" : "border-border text-muted-foreground")}>{s}</button>
        ))}
        <Input placeholder="Rechercher dans les messages…" value={q} onChange={(e) => setQ(e.target.value)} className="ml-auto h-8 w-72" />
      </div>
      <div className="glass min-h-0 flex-1 overflow-auto rounded-2xl font-mono text-xs">
        <table className="w-full">
          <tbody>
            {(data ?? []).map((r) => (
              <tr key={r.id} className="cursor-pointer border-b border-border/60 align-top hover:bg-muted/40" onClick={() => setOpen(open === r.id ? null : r.id)}>
                <td className="w-28 whitespace-nowrap px-3 py-1.5 text-muted-foreground" title={r.created_at}>{formatDistanceToNow(new Date(r.created_at), { locale: fr, addSuffix: true })}</td>
                <td className={cn("w-14 px-2 py-1.5 font-semibold uppercase", LEVEL_CLASS[r.level])}>{r.level}</td>
                <td className="w-16 px-2 py-1.5 text-muted-foreground">{r.source}</td>
                <td className="px-2 py-1.5">
                  <div className="whitespace-pre-wrap break-words">{r.message}</div>
                  {open === r.id && (
                    <pre className="mt-2 max-h-96 overflow-auto rounded bg-muted p-2 text-[11px] text-muted-foreground">
                      {JSON.stringify({ request_id: r.request_id, user_id: r.user_id, context: r.context }, null, 2)}
                      {r.stack ? `\n\n${r.stack}` : ""}
                    </pre>
                  )}
                </td>
              </tr>
            ))}
            {data && data.length === 0 && (
              <tr><td className="px-3 py-6 text-center text-muted-foreground" colSpan={4}>Aucune entrée pour ces filtres.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ConsolePage() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4">
        <h1 className="font-heading text-2xl font-bold">Console</h1>
        <p className="text-sm text-muted-foreground">Tout ce qui se passe dans le CRM : santé, tâches planifiées, connexions et journaux détaillés.</p>
      </div>
      <Tabs defaultValue="health" className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="health">Santé</TabsTrigger>
          <TabsTrigger value="logs">Journaux</TabsTrigger>
        </TabsList>
        <TabsContent value="health"><HealthTab /></TabsContent>
        <TabsContent value="logs" className="min-h-0 flex-1"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
